/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Modal,
  Input,
  message,
  Typography,
  Tabs,
  Select,
  Tag as AntTag,
  SelectProps,
  Button,
} from 'antd';
import { useIntl } from 'react-intl';
import { ModalProps } from 'antd/lib/modal';
import { useMutation, queryCache } from 'react-query';

import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { DbFile } from '~src/types/db';
import { addTagFile, deleteTagFile, editFile } from '~src/services/api';
import { appLog } from '~src/utils/log';
import { useProjectId } from '~src/hooks/useProjectId';
import { ModalTitle } from '~components/_common/ModalTitle/ModalTitle';
import { useFiles } from '~src/hooks/api-hooks/useFiles';
import { parseFileName } from '~src/utils/file';
import ModalLoader from '~components/atoms/ModalLoader/ModalLoader';

import { Tag } from '~src/services/api/archiveSettings';
import { useArchiveSettings } from '~src/hooks/api-hooks/useArchiveSettings';
import { mapSecGroupsToSections } from './RightsModal/Tabs/tabs.util';

import { DB_STATE } from '~src/types/enums';

export interface EditFileModalProps extends Partial<ModalProps> {
  file?: DbFile;
}

export interface SimpleTag {
  id: number;
  title: React.ReactNode;
  state: number;
}

interface TagsGroupsColumnProps extends Partial<SelectProps<any>> {
  options: SelectProps<any>['options'];
  selectedGroups?: SimpleTag[];
  //setSelectedGroups: (groups: SimpleTag[]) => void;
}
export const EditFileModal: React.FC<EditFileModalProps> = ({
  file,
  onCancel,
  ...modalProps
}) => {
  const intl = useIntl();
  const projectId = useProjectId();
  const archiveSettings = useArchiveSettings();
  const isTagsOn = archiveSettings?.data?.resArchiveSettings?.is_tags_on;

  const filesQuery = useFiles({ folderId: String(file?.folder_id) });
  const currentFile = filesQuery?.data?.filter((v) => v.id === file?.id)[0];
  const tagsData = archiveSettings.data?.resTags?.filter(
    (tag) => tag.state !== DB_STATE.REMOVED
  );
  const [nameVal, setNameVal] = useState('');
  const [descriptionVal, setDescriptionVal] = useState(file?.description || '');

  const selectedGroups =
    tagsData?.filter((tag) => currentFile?.tags?.includes(tag.id)) || [];
  const [activeTab, setActiveTab] = useState('1');
  const nameInputRef = useRef<Input>(null);

  const tagGroupsOptions = useMemo(() => {
    return (
      tagsData
        ?.filter((tag) => tag.state !== DB_STATE.REMOVED)
        .map((tag) => ({
          value: tag.id,
          label: tag.title,
        })) || []
    );
  }, [tagsData]);

  const [doEditFile, { isLoading }] = useMutation(editFile, {
    onSuccess: (theFile) => {
      // hide modal
      // @ts-ignore-line
      onCancel && onCancel();

      queryCache.invalidateQueries('files');
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'file-edit-failed' }));
      appLog('EditFileModal -> editFile -> item', item);
      appLog('EditFileModal -> editFile -> err', error);
    },
  });

  const [doAddTagFile, { isLoading: isAddingTag }] = useMutation(addTagFile, {
    onSuccess: async (addedTag, {}) => {
      // hide modal
      // @ts-ignore
      //onDone ? onDone() : onCancel && onCancel();
      queryCache.invalidateQueries('files');
      queryCache.invalidateQueries('archiveSettingsList');
      message.success(intl.formatMessage({ id: 'changes-saved-successfully' }));
    },
    onError: (error, item) => {
      message.error(intl.formatMessage({ id: 'tag-add-file-failed' }));
      appLog('doAddTagFile -> doAddTagFile -> items', item);
      appLog('doAddTagFile -> addTagFile -> err', error);
    },
  });

  const [doDeleteTagFile, { isLoading: isDeletingTag }] = useMutation(
    deleteTagFile,
    {
      onSuccess: async (deletedTag, {}) => {
        // hide modal
        // @ts-ignore
        //onDone ? onDone() : onCancel && onCancel();
        queryCache.invalidateQueries('files');
        queryCache.invalidateQueries('archiveSettingsList');
        message.success(
          intl.formatMessage({ id: 'changes-saved-successfully' })
        );
      },
      onError: (error, item) => {
        message.error(intl.formatMessage({ id: 'tag-remove-file-failed' }));
        appLog('doDeleteTagFile -> doDeleteTagFile -> items', item);
        appLog('doDeleteTagFile -> addTagFile -> err', error);
      },
    }
  );

  const modalLoading = isAddingTag || isDeletingTag;

  const parsedName = useMemo(() => {
    return parseFileName(file?.title || '');
  }, [file]);

  // update values based on selected file
  useEffect(() => {
    setNameVal(parsedName.name);
    setDescriptionVal(file?.description || '');
  }, [file, parsedName.name]);

  useEffect(() => {
    if (modalProps.visible) {
      // fetch files if data is older than 30sec
      if (!filesQuery.data || Date.now() - filesQuery.updatedAt > 30 * 1000) {
        filesQuery.refetch();
      }
    }
  }, [modalProps.visible, filesQuery]);

  useEffect(() => {
    if (modalProps.visible) {
      // focus name input on modal show
      setTimeout(() => nameInputRef.current?.focus(), 350);
    }
  }, [modalProps.visible]);

  const getFullName = () => {
    let fullName = nameVal.trim();
    if (parsedName.extension) fullName += `.${parsedName.extension}`;

    return fullName;
  };

  const isValid = () => {
    if (!file || !nameVal) return false;

    // check if file with the same name exists
    if (filesQuery.data?.length) {
      const filename = getFullName();
      const exists = filesQuery.data.some(
        (v) => v.id !== file.id && v.title.trim() === filename
      );

      if (exists) return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!file || !isValid()) return;

    doEditFile({
      data: {
        ...file,
        title: getFullName(),
        description: descriptionVal,
      },
    });
  };

  const TagsGroupsColumn: React.FC<TagsGroupsColumnProps> = ({
    options,
    selectedGroups = [],
    // setSelectedGroups,
    ...selectProps
  }) => {
    const [tagsVisible, setTagsVisible] = useState(false);
    const [isOpen, setIsOpen] = useState(false);

    return (
      <Select
        className="borderless"
        css={{ width: '100%' }}
        mode="tags"
        open={isOpen}
        onDropdownVisibleChange={(isItOpen) => {
          if (isOpen) {
            setIsOpen(isItOpen);
          }
        }}
        onClick={() => setIsOpen((prevState) => !prevState)}
        optionFilterProp="label"
        dropdownMatchSelectWidth={false}
        maxTagCount={tagsVisible ? undefined : 3}
        maxTagPlaceholder={(hidden) => `+${hidden.length}`}
        maxTagTextLength={5}
        options={options}
        value={selectedGroups.map((g) => g.id)}
        tagRender={({ value, label, closable, ...props }) => {
          /** if it's `+n` tag */
          const isTrailing = !value;
          const group = !isTrailing
            ? selectedGroups.find((v) => v.id === value)
            : undefined;
          const text = !isTrailing ? group?.title : label;

          return (
            <AntTag
              css={[isTrailing && { cursor: 'pointer' }]}
              color={'#1f5fa6'}
              closable={closable && !selectProps.disabled}
              onClick={isTrailing ? () => setTagsVisible(true) : undefined}
              {...props}>
              {text || '??'}
            </AntTag>
          );
        }}
        onSelect={(value) => {
          const newGroup = options?.find((option) => option.value === value);
          if (newGroup) {
            /*    setSelectedGroups([
              ...selectedGroups,
              { id: value, title: newGroup.label },
            ]); */
            if (file?.id) {
              doAddTagFile({
                projectId: String(projectId),
                versionedFileId: file?.id,
                id: value,
              });
            }
          }
          setTagsVisible(false);
        }}
        onDeselect={(value) => {
          /*  setSelectedGroups(
            selectedGroups.filter((group) => group.id !== value)
          ); */
          if (file?.id) {
            doDeleteTagFile({
              projectId: String(projectId),
              versionedFileId: file?.id,
              id: value,
            });
          }

          setIsOpen(false);
        }}
        {...selectProps}
      />
    );
  };

  return (
    <Modal
      title={
        <ModalTitle
          text={intl.formatMessage({ id: 'edit-file' })}
          icon={['far', 'pen']}
        />
      }
      okText={intl.formatMessage({ id: 'save' })}
      cancelText={intl.formatMessage({ id: 'cancel' })}
      width={420}
      centered
      okButtonProps={{ disabled: !isValid(), loading: isLoading }}
      onOk={handleSubmit}
      onCancel={onCancel}
      footer={
        activeTab === '1' ? (
          <Fragment>
            <Button key="back" onClick={onCancel}>
              {intl.formatMessage({ id: 'cancel' })}
            </Button>
            <Button
              key="submit"
              type="primary"
              loading={isLoading}
              disabled={!isValid()}
              onClick={handleSubmit}>
              {intl.formatMessage({ id: 'save' })}
            </Button>
          </Fragment>
        ) : (
          <Button key="back" onClick={onCancel}>
            {intl.formatMessage({ id: 'close' })}
          </Button>
        )
      }
      {...modalProps}>
      {modalLoading && <ModalLoader />}
      <Tabs
        style={{ marginTop: '-40px ' }}
        defaultActiveKey="1"
        onChange={setActiveTab}>
        <Tabs.TabPane
          style={{ marginTop: 8 }}
          tab={intl.formatMessage({ id: 'general-tab' })}
          key="1">
          <Typography.Paragraph
            css={{ '&.ant-typography': { marginBottom: 8 } }}>
            {intl.formatMessage({ id: 'from' })} <b>{parsedName.filename}</b>
          </Typography.Paragraph>
          <Input
            ref={nameInputRef}
            placeholder={intl.formatMessage({ id: 'file-name' })}
            autoCapitalize="words"
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            suffix={
              parsedName.extension ? `.${parsedName.extension}` : <span />
            }
            value={nameVal}
            onChange={({ target: { value } }) => setNameVal(value)}
          />
          <Input.TextArea
            css={{
              marginTop: 10,
            }}
            placeholder={intl.formatMessage({ id: 'description' })}
            value={descriptionVal}
            onChange={({ target: { value } }) => setDescriptionVal(value)}
          />
        </Tabs.TabPane>
        {isTagsOn && (
          <Tabs.TabPane
            style={{ marginTop: 8 }}
            tab={intl.formatMessage({ id: 'tags' })}
            key="2">
            <Typography.Paragraph>
              {intl.formatMessage({ id: 'tags' })}
            </Typography.Paragraph>

            <TagsGroupsColumn
              options={tagGroupsOptions}
              selectedGroups={selectedGroups}
            />
          </Tabs.TabPane>
        )}
      </Tabs>
    </Modal>
  );
};

const styleFn: ThemedStyleFn = (theme) => ({});
